import { Location, NgForOf, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { FormsModule } from '@angular/forms';
import { catchError, forkJoin, of, Subject, takeUntil } from 'rxjs';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import {
  ICampaign,
  ICampaignPhase,
  ICampaignData,
  IChannelItem,
  ICreatePhaseRequest,
  IUpdatePhaseRequest
} from '../../../models/campaign/campaign.interface';
import { TypeEnum } from '../../../models/template/type.enum';
import { CampaignService } from '../../../services/campaign.service';
import { TemplateService } from '../../../services/template.service';
import { ToastBarService } from '../../../services/toast-bar.service';
import { AccountInfo } from '@azure/msal-browser';
import { AccountInfoService } from '../../../services/account-info.service';
import { ChannelDetailsComponent } from '../channel-details/channel-details.component';
import { CHANNEL_CONFIGS } from '../channel-details/channel-config';
import { Template } from '../../../models/template/template.interface';

@Component({
  selector: 'app-create-campaign',
  imports: [
    DialogModule,
    ProgressSpinnerModule,
    FormsModule,
    NgForOf,
    NgIf,
    ChannelDetailsComponent,
    AccordionModule
  ],
  templateUrl: './edit-campaign.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-campaign.component.scss'
})
export class EditCampaignComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly templateService = inject(TemplateService);
  private readonly campaignService = inject(CampaignService);
  private readonly toastBarService = inject(ToastBarService);
  private readonly accountInfoService = inject(AccountInfoService);

  loading = false;
  campaign?: ICampaign;
  campaignData: ICampaignData;
  isInProgress = false;
  TypeEnum = TypeEnum;

  // Phase management
  showAddPhaseForm = false;
  editingPhase: ICampaignPhase | null = null;
  phases: ICampaignPhase[] = [];

  // New phase form
  newPhaseName = '';
  newPhaseOrder = 1;

  // User info for phase operations
  user?: AccountInfo;

  // Channel schedule
  plannedDate: string | undefined;

  readonly channelConfigs = CHANNEL_CONFIGS;

  _destroy$ = new Subject<void>();

  constructor() {
    const nav = this.router.currentNavigation();
    this.campaignData = nav?.extras?.state?.['campaignData'];
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngOnInit(): void {
    // Get user info
    this.accountInfoService.accountInfo$
      .pipe(takeUntil(this._destroy$))
      .subscribe((data) => {
        this.user = data;
      });

    this.loadCampaign();
  }

  parseDateFromName(templateName: string, returnWithSlashes: boolean = false): string {
    if (!templateName) return "N/A";

    const FORMAT_REGEX = /^\d{8}_/;
    if (!FORMAT_REGEX.test(templateName)) return "N/A";

    const datePart = templateName.slice(0, 8);
    const year = datePart.slice(0, 4);
    const month = datePart.slice(4, 6);
    const day = datePart.slice(6, 8);

    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    const date = new Date(y, m - 1, d);
    const isValidDate =
      date.getFullYear() === y &&
      date.getMonth() === m - 1 &&
      date.getDate() === d;

    if (!isValidDate) return "N/A";

    if (returnWithSlashes) {
      return `${day}/${month}/${year}`;
    }
    return `${day}.${month}.${year}`;
  }

  loadCampaign() {
    this.loading = true;

    this.campaignService.getCampaign(this.campaignData.campaignId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (data: ICampaign) => {
          console.log("Campaign:", data);
          this.campaign = data;
          this.isInProgress = this.campaign.isInProgress ?? false;
          this.campaignData.campaignId = data.campaignId!;
          this.campaignData.campaignName = data.campaignName;

          // Sort phases by order
          this.phases = data.phases
            ? [...data.phases].sort((a, b) => a.phaseOrder - b.phaseOrder)
            : [];

          // Load templates for each phase
          this.loadPhaseTemplates();

          this.loading = false;
        },
        error: (error: any) => {
          console.error("error get campaign by id", error);
          this.toastBarService.showToastError("Error while fetching the campaign!");
          this.loading = false;
        }
      });
  }

  private loadPhaseTemplates() {
    this.phases.forEach(phase => {
      if (phase.channels?.emailChannels) {
        this.loadChannelTemplates(phase, 'email', phase.channels.emailChannels);
      }
      if (phase.channels?.smsChannels) {
        this.loadChannelTemplates(phase, 'sms', phase.channels.smsChannels);
      }
      if (phase.channels?.webPushChannels) {
        this.loadChannelTemplates(phase, 'webPush', phase.channels.webPushChannels);
      }
      if (phase.channels?.pushChannels) {
        this.loadChannelTemplates(phase, 'push', phase.channels.pushChannels);
      }
      if (phase.channels?.viberChannels) {
        this.loadChannelTemplates(phase, 'viber', phase.channels.viberChannels);
      }
      // Initialize rcsChannels if not present (backend might not return it)

      if (phase.channels?.rcsChannels) {
        this.loadChannelTemplates(phase, 'rcs', phase.channels.rcsChannels);
      }

      if (phase.channels?.whatsappChannels) {
        this.loadChannelTemplates(phase, 'whatsapp', phase.channels.whatsappChannels);
      }
    });
  }

  private loadChannelTemplates(
    phase: ICampaignPhase,
    channelType: 'email' | 'sms' | 'webPush' | 'push' | 'viber' | 'rcs' | 'whatsapp',
    templateIds: (string | null)[]
  ) {

    console.log("LOAD CHANNELS:", phase);
    const channelKey = `${channelType}Channels`;

    if (!phase.channels) {
      phase.channels = {};
    }

    if (!phase.channels[channelKey as keyof typeof phase.channels]) {
      (phase.channels as any)[channelKey] = [];
    }

    const requests = templateIds
      .filter((id): id is string => !!id)
      .map(templateId =>
        this.templateService.getTemplateById(templateId).pipe(
          catchError(err => {
            console.warn(`Failed to load template ${templateId} for ${channelType}`, err);
            return of(null);
          })
        )
      );

    forkJoin(requests)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (templates) => {
          this.updatePhaseChannelsWithTemplates(templates, phase, channelKey);
        }
      });
  }

  private updatePhaseChannelsWithTemplates(templates: any[], phase: ICampaignPhase, channelKey: string) {
    const enriched: IChannelItem[] = [];
    const seen = new Set<string>();
    templates.forEach((template) => {
      if (template?.templateName && !seen.has(template.templateId)) {
        seen.add(template.templateId);
        enriched.push({
          id: template.templateId,
          name: template.templateName,
          plannedDate: template.plannedDate ?? null
        });
      }
    });
    (phase.channels as any)[channelKey] = enriched;
  }

  goBack() {
    this.location.back();
  }

  // ==================== PHASE MANAGEMENT ====================

  showAddPhase() {
    this.showAddPhaseForm = true;
    this.editingPhase = null;
    this.newPhaseName = '';
    this.newPhaseOrder = this.phases.length + 1;
  }

  hideAddPhase() {
    this.showAddPhaseForm = false;
    this.editingPhase = null;
    this.newPhaseName = '';
  }

  createPhase() {
    if (!this.newPhaseName.trim()) {
      this.toastBarService.showToastError('Phase name is required!');
      return;
    }

    const request: ICreatePhaseRequest = {
      phaseName: this.newPhaseName.trim(),
      phaseOrder: this.newPhaseOrder,
      userInfo: {
        name: this.user?.name,
        username: this.user?.username,
      },
      channels: {
        emailChannels: [],
        smsChannels: [],
        webPushChannels: [],
        pushChannels: [],
        viberChannels: [],
        rcsChannels: [],
        whatsappChannels: []
      }
    };

    this.campaignService.createPhase(this.campaignData.campaignId!, request)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: ICampaignPhase) => {
          this.toastBarService.showToastSuccess('Phase created successfully!');
          this.phases.push(res);
          this.phases.sort((a, b) => a.phaseOrder - b.phaseOrder);
          this.hideAddPhase();
        },
        error: (err: any) => {
          console.error('Error creating phase', err);
          this.toastBarService.showToastError('Failed to create phase!');
        }
      });
  }

  editPhase(phase: ICampaignPhase) {
    this.editingPhase = phase;
    this.newPhaseName = phase.phaseName;
    this.newPhaseOrder = phase.phaseOrder;
    this.showAddPhaseForm = true;
  }

  updatePhase() {
    if (!this.editingPhase || !this.newPhaseName.trim()) {
      this.toastBarService.showToastError('Phase name is required!');
      return;
    }

    const request: IUpdatePhaseRequest = {
      phaseId: this.editingPhase.phaseId!,
      phaseName: this.newPhaseName.trim(),
      phaseOrder: this.newPhaseOrder,
      userInfo: {
        name: this.user?.name,
        username: this.user?.username,
      },
      channels: this.editingPhase.channels
    };

    this.campaignService.updatePhase(this.campaignData.campaignId!, request)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: any) => {
          this.toastBarService.showToastSuccess('Phase updated successfully!');
          // Update local phase data
          const index = this.phases.findIndex(p => p.phaseId === this.editingPhase!.phaseId);
          if (index !== -1) {
            this.phases[index] = {
              ...this.editingPhase,
              phaseName: this.newPhaseName.trim(),
              phaseOrder: this.newPhaseOrder
            };
          }
          this.phases.sort((a, b) => a.phaseOrder - b.phaseOrder);
          this.hideAddPhase();
        },
        error: (err: any) => {
          console.error('Error updating phase', err);
          this.toastBarService.showToastError('Failed to update phase!');
        }
      });
  }

  deletePhase(phase: ICampaignPhase) {
    if (!confirm(`Are you sure you want to delete phase "${phase.phaseName}"?`)) {
      return;
    }

    this.campaignService.deletePhase(this.campaignData.campaignId!, phase.phaseId!)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: any) => {
          this.toastBarService.showToastSuccess('Phase deleted successfully!');
          this.phases = this.phases.filter(p => p.phaseId !== phase.phaseId);
        },
        error: (err: any) => {
          console.error('Error deleting phase', err);
          this.toastBarService.showToastError('Failed to delete phase!');
        }
      });
  }

  addTemplateToPhase(phase: ICampaignPhase, type: TypeEnum, template: Template) {
    const suffix = this.channelConfigs.find(c => c.typeEnum === type)?.typeSuffix;
    if (!suffix || !template?.templateId) return;
    const channelKey = `${suffix}Channels`;

    if (!phase.channels) phase.channels = {};
    if (!(phase.channels as any)[channelKey]) (phase.channels as any)[channelKey] = [];

    const list = (phase.channels as any)[channelKey] as IChannelItem[];
    if (list.some(item => item?.id === template.templateId)) return; // already present

    list.push({
      id: template.templateId,
      name: template.templateName,
      plannedDate: template.plannedDate ?? null
    });
  }

  removeTemplateFromPhase(phase: ICampaignPhase, templateId: string, templateType: TypeEnum) {
    this.handleTemplateTypeFiltration(phase, templateType, templateId);

    // Clean channels - convert objects {id, name} to strings before sending to API
    const cleanedChannels = this.cleanChannelsForApi(phase.channels);

    const request: IUpdatePhaseRequest = {
      phaseId: phase.phaseId!,
      phaseName: phase.phaseName,
      phaseOrder: phase.phaseOrder,
      channels: cleanedChannels
    };

    this.campaignService.updatePhase(this.campaignData.campaignId!, request)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: any) => {
          this.toastBarService.showToastSuccess('Template removed from phase!');
        },
        error: (err: any) => {
          console.error('Error updating phase', err);
          this.toastBarService.showToastError('Failed to remove template!');
        }
      });
  }

  // Helper method to clean channels - convert objects {id, name} back to string IDs
  private cleanChannelsForApi(channels: any): any {
    if (!channels) return {};

    const cleanArray = (arr: any[]): string[] => {
      if (!arr) return [];
      return arr
        .map((item: any) => {
          // If it's an object with id property, extract the id
          if (item && typeof item === 'object' && item.id) {
            return item.id;
          }
          // If it's already a string, return it
          if (typeof item === 'string') {
            return item;
          }
          // Filter out null/undefined
          return null;
        })
        .filter((id): id is string => id !== null);
    };

    return {
      emailChannels: cleanArray((channels as any).emailChannels || []),
      smsChannels: cleanArray((channels as any).smsChannels || []),
      webPushChannels: cleanArray((channels as any).webPushChannels || []),
      pushChannels: cleanArray((channels as any).pushChannels || []),
      viberChannels: cleanArray((channels as any).viberChannels || []),
      rcsChannels: cleanArray((channels as any).rcsChannels || []),
      whatsappChannels: cleanArray((channels as any).whatsappChannels || [])
    };
  }

  private handleTemplateTypeFiltration(phase: ICampaignPhase, templateType: TypeEnum, templateId: string) {
    if (!phase.channels) return;

    switch (templateType) {
      case TypeEnum.EMAIL:
        if (phase.channels.emailChannels) {
          phase.channels.emailChannels = phase.channels.emailChannels.filter(id => id !== templateId);
        }
        if ((phase.channels as any).emailChannels) {
          (phase.channels as any).emailChannels = (phase.channels as any).emailChannels.filter((item: any) => item?.id !== templateId);
        }
        break;
      case TypeEnum.SMS:
        if (phase.channels.smsChannels) {
          phase.channels.smsChannels = phase.channels.smsChannels.filter(id => id !== templateId);
        }
        if ((phase.channels as any).smsChannels) {
          (phase.channels as any).smsChannels = (phase.channels as any).smsChannels.filter((item: any) => item?.id !== templateId);
        }
        break;
      case TypeEnum.WEBPUSH:
        if (phase.channels.webPushChannels) {
          phase.channels.webPushChannels = phase.channels.webPushChannels.filter(id => id !== templateId);
        }
        if ((phase.channels as any).webPushChannels) {
          (phase.channels as any).webPushChannels = (phase.channels as any).webPushChannels.filter((item: any) => item?.id !== templateId);
        }
        break;
      case TypeEnum.PUSH:
        if (phase.channels.pushChannels) {
          phase.channels.pushChannels = phase.channels.pushChannels.filter(id => id !== templateId);
        }
        if ((phase.channels as any).pushChannels) {
          (phase.channels as any).pushChannels = (phase.channels as any).pushChannels.filter((item: any) => item?.id !== templateId);
        }
        break;
      case TypeEnum.VIBER:
        if ((phase.channels as any).viberChannels) {
          (phase.channels as any).viberChannels = (phase.channels as any).viberChannels.filter((id: string | null) => id !== templateId);
        }
        if ((phase.channels as any).viberChannels) {
          (phase.channels as any).viberChannels = (phase.channels as any).viberChannels.filter((item: any) => item?.id !== templateId);
        }
        break;
      case TypeEnum.RCS:
        if ((phase.channels as any).rcsChannels) {
          (phase.channels as any).rcsChannels = (phase.channels as any).rcsChannels.filter((id: string | null) => id !== templateId);
        }
        if ((phase.channels as any).rcsChannels) {
          (phase.channels as any).rcsChannels = (phase.channels as any).rcsChannels.filter((item: any) => item?.id !== templateId);
        }
        break;
      case TypeEnum.WHATSAPP:
        if ((phase.channels as any).whatsappChannels) {
          (phase.channels as any).whatsappChannels = (phase.channels as any).whatsappChannels.filter((id: string | null) => id !== templateId);
        }
        if ((phase.channels as any).whatsappChannels) {
          (phase.channels as any).whatsappChannels = (phase.channels as any).whatsappChannels.filter((item: any) => item?.id !== templateId);
        }
        break;
    }
  }

  // Helper method to get display channels for a phase
  // Returns only enriched objects (with id and name), filters out raw string IDs
  getPhaseChannels(phase: ICampaignPhase, channelType: string, test: boolean = false): any[] {
    if (!phase.channels) return [];
    const channelKey = `${channelType}Channels`;
    const channels = (phase.channels as any)[channelKey] || [];
    // Return only enriched objects (with id and name), not string IDs
    return channels.filter((item: any) =>
      item && typeof item === 'object' && item.id && item.name
    );
  }

  // Get the next channel number suffix (e.g., if there are 3 email channels, returns 4)
  getNextChannelNumber(phase: ICampaignPhase, channelType: string): number {
    return this.getPhaseChannels(phase, channelType).length + 1;
  }

  setTemplatePlannedDate(channel: IChannelItem, event: any): void {
    // ctrlKey is used here because event is either date or event
    if (event.ctrlKey === false) {
      this.templateService.updatePlannedDate({templateName: channel.name})
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => {
            channel.plannedDate = null;
            this.toastBarService.showToastSuccess('Planned date cleared!');
          },
          error: (err: any) => {
            console.error('Error clearing planned date', err);
            this.toastBarService.showToastError('Failed to clear planned date!');
          }
        });
      return;
    }

    const month = event.getMonth() + 1;
    const day = event.getDate();
    const year = event.getFullYear();
    const plannedDateString = `${month}/${day}/${year}`;

    this.templateService.updatePlannedDate({
      templateName: channel.name,
      plannedDate: plannedDateString
    })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          channel.plannedDate = plannedDateString;
          this.toastBarService.showToastSuccess('Planned date saved!');
        },
        error: (err: any) => {
          console.error('Error saving planned date', err);
          this.toastBarService.showToastError('Failed to save planned date!');
        }
      });
  }
}
